import { createSelector } from '@reduxjs/toolkit';
import { selectTrackScores } from 'features/system-stats/systemStatsSlice';
import { TrackScore } from 'utils/webrtc/stats/types';

// Sentinel key for the bucket that holds tracks whose participant cannot be
// resolved yet (no feedId mapping). Surfaced as its own group so unmeasurable
// scores are not silently dropped from the UI.
export const UNRESOLVED = '__unresolved__';

// Picks the track that best represents a participant's quality in the summary
// row. Lowest non-null score wins (diagnostic UI surfaces the worst case). If
// every track is unmeasurable, surface the first one so its reason still
// appears.
export const pickSummaryTrack = (tracks: TrackScore[]): TrackScore | null => {
  if (tracks.length === 0) {
    return null;
  }
  let worst: TrackScore | null = null;
  for (const t of tracks) {
    if (t.score !== null && (worst === null || t.score < (worst.score as number))) {
      worst = t;
    }
  }
  return worst ?? tracks[0];
};

const groupByFeed = (scores: TrackScore[]): Array<[string, TrackScore[]]> => {
  const map = new Map<string, TrackScore[]>();
  for (const s of scores) {
    const key = s.feedId ?? UNRESOLVED;
    const list = map.get(key);
    if (list) {
      list.push(s);
    } else {
      map.set(key, [s]);
    }
  }
  return [...map.entries()];
};

export const selectTrackScoresByFeed = createSelector([selectTrackScores], groupByFeed);
